import React, { useState } from 'react'
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Input,
  Text,
  InputGroup,
  InputRightElement,
  SimpleGrid,
  Image,
  Box,
  Spinner,
  Flex,
  useToast,
  AspectRatio,
  Alert,
  AlertIcon,
} from '@chakra-ui/react'
import { FaSearch } from 'react-icons/fa'
import { useQuery } from 'react-query'
import { api } from '../../../../services/api'

export function ModalBuscarImagensProduto({ isOpen, onClose, onSelectImage, artigoId }) {
  const [searchTerm, setSearchTerm] = useState('')
  const toast = useToast()

  // Utilizamos o React Query para gerenciar o estado de busca
  const { data, isLoading, error, refetch, isFetching, isError } = useQuery(
    ['productImages', searchTerm, artigoId],
    async () => {
      if (!searchTerm.trim()) return { urlImagem: [] }

      const response = await api.get('/crm-interno/busca-imagem-artigo', {
        params: {
          id: artigoId,
          nome_produto: searchTerm,
        },
      })

      return response.data
    },
    {
      enabled: false, // Não executa automaticamente ao montar o componente
      keepPreviousData: true, // Mantém os dados anteriores enquanto carrega novos
      staleTime: 1000 * 60 * 10, // Cache válido por 10 minutos
      onError: (err) => {
        console.error('Erro ao buscar imagens:', err)
        toast({
          title: 'Erro',
          description: 'Não foi possível buscar as imagens',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      },
    }
  )

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      toast({
        title: 'Atenção',
        description: 'Digite um nome ou código de produto para buscar',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    refetch()
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleSelectImage = (image) => {
    onSelectImage(image)
    onClose()
  }

  const hasImages = data?.urlImagem?.length > 0

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Buscar Imagens de Produtos</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb={2}>Digite um nome ou código de produto para buscar imagens:</Text>
          <InputGroup mb={4}>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Nome ou código do produto"
              isDisabled={isFetching}
            />
            <InputRightElement>
              <Button size="sm" onClick={handleSearch} isLoading={isFetching}>
                <FaSearch />
              </Button>
            </InputRightElement>
          </InputGroup>

          {isFetching ? (
            <Flex justifyContent="center" my={8}>
              <Spinner size="xl" />
            </Flex>
          ) : isError ? (
            <Alert status="error">
              <AlertIcon />
              Erro ao buscar imagens. Verifique sua conexão e tente novamente.
            </Alert>
          ) : data && !hasImages && searchTerm.trim() !== '' ? (
            <Alert status="info">
              <AlertIcon />
              Nenhuma imagem encontrada para este produto.
            </Alert>
          ) : hasImages ? (
            <SimpleGrid columns={2} spacing={4} maxH="400px" overflowY="auto">
              {data.urlImagem.map((image, index) => (
                <Box
                  key={index}
                  borderWidth="1px"
                  borderRadius="md"
                  p={2}
                  cursor="pointer"
                  _hover={{ boxShadow: 'md', borderColor: 'blue.500' }}
                  onClick={() => handleSelectImage(image)}
                >
                  <AspectRatio ratio={4 / 3} mb={2}>
                    <Image
                      src={image.LINK_IMAGEM}
                      alt={image.DESCRICAO_IMAGEM || 'Imagem do produto'}
                      objectFit="cover"
                      borderRadius="sm"
                      fallback={<Spinner />}
                    />
                  </AspectRatio>
                  <Text fontSize="xs" noOfLines={2}>
                    {image.DESCRICAO_IMAGEM || 'Sem descrição'}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Código: {image.CODPROD}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
